import type { LucideIcon } from "lucide-react"
import {
  BarChart3,
  BookOpen,
  CreditCard,
  Inbox,
  Megaphone,
  Mail,
  Receipt,
  Settings2,
  Target,
  TrendingUp,
} from "lucide-react"

import type { NotificationCategory } from "@/types/notifications"

export type NotificationsCentreCategoryLink = {
  id: NotificationCategory
  label: string
  icon: LucideIcon
}

/** Ordered category links for the notifications centre sidebar (excludes Inbox). */
export const NOTIFICATIONS_CENTRE_CATEGORY_LINKS = [
  { id: "trade", label: "Trade", icon: Receipt },
  { id: "performance", label: "Performance", icon: Target },
  { id: "statistics", label: "Statistics", icon: BarChart3 },
  { id: "system", label: "System", icon: Settings2 },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "announcement", label: "Announcements", icon: Megaphone },
  { id: "marketing", label: "Marketing", icon: Mail },
  { id: "market", label: "Market", icon: TrendingUp },
  { id: "learning", label: "Learning", icon: BookOpen },
] as const satisfies ReadonlyArray<NotificationsCentreCategoryLink>

/** Hidden from the notifications centre sidebar icon rail; inbox/routes may still resolve labels. */
export const NOTIFICATIONS_CENTRE_HIDDEN_SIDEBAR_CATEGORIES = new Set<NotificationCategory>([
  "trade",
  "statistics",
  "marketing",
  "learning",
  "market",
])

export const NOTIFICATIONS_CENTRE_SIDEBAR_CATEGORY_LINKS =
  NOTIFICATIONS_CENTRE_CATEGORY_LINKS.filter(
    (link) => !NOTIFICATIONS_CENTRE_HIDDEN_SIDEBAR_CATEGORIES.has(link.id)
  )

export const NOTIFICATIONS_CENTRE_INBOX_LINK = {
  id: "inbox",
  label: "Inbox",
  icon: Inbox,
} as const

const CATEGORY_LABELS = Object.fromEntries(
  NOTIFICATIONS_CENTRE_CATEGORY_LINKS.map((link) => [link.id, link.label])
) as Record<NotificationCategory, string>

export function isNotificationsCentreCategory(
  value: string | null | undefined
): value is NotificationCategory {
  if (!value) return false
  return NOTIFICATIONS_CENTRE_CATEGORY_LINKS.some((link) => link.id === value)
}

export function getNotificationsCentreCategoryLabel(
  category: NotificationCategory
): string {
  return CATEGORY_LABELS[category] ?? category
}
